import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ImageSlider } from './ui/ImageSlider';
import { mockData } from '../data/mock';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

const Vehicles = ({ onBookingClick }) => {
  const { t } = useThemeLanguage();

  const vehicleFeatures = [
    t('vehicles.features.spacious'),
    t('vehicles.features.acoustic'),
    t('vehicles.features.suspension'),
    t('vehicles.features.seats'),
    t('vehicles.features.hybrid'),
  ];

  return (
    <section className="py-20 bg-white dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 dark:text-white mb-6">
            {t('vehicles.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gray-400 to-transparent mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t('vehicles.subtitle')}
          </p>
        </div>

        {/* Vehicle Card */}
        <div className="max-w-2xl mx-auto">
          {mockData.vehicles.map((vehicle) => (
            <Card 
              key={vehicle.id}
              className="group overflow-hidden bg-white dark:bg-gray-700 border-0 shadow-lg hover:shadow-2xl transition-all duration-500"
            >
              {/* Vehicle Image */}
              <div className="aspect-[16/10] overflow-hidden relative">
                <ImageSlider images={vehicle.images} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                {/* Category Badge */}
                <Badge className="absolute top-4 left-4 bg-white/90 text-black border-0 font-medium">
                  {t('vehicles.category')}
                </Badge>
              </div>

              <CardContent className="p-8">
                {/* Vehicle Name */}
                <h3 className="text-3xl font-light text-gray-900 dark:text-white mb-4 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors text-center">
                  {vehicle.name}
                </h3>

                {/* Capacity Info */}
                <div className="flex items-center justify-center gap-8 mb-6 text-gray-600 dark:text-gray-300">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                    <span className="text-sm">{vehicle.passengers} {t('vehicles.passengers')}</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 4v12l-4-2-4 2V4M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm">{vehicle.luggage} {t('vehicles.luggage')}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-8">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4 text-center">{t('vehicles.includedEquipment')}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {vehicleFeatures.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2 flex-shrink-0"></div>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-4">
                  <Button 
                    onClick={onBookingClick}
                    data-testid="vehicle-book-btn"
                    className="w-full bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 py-3"
                  >
                    {t('vehicles.bookVehicle')}
                  </Button>
                  <a 
                    href="https://wa.me/33749777371?text=Bonjour%2C%20je%20souhaiterais%20obtenir%20un%20devis%20personnalis%C3%A9%20pour%20un%20trajet.%20Merci%20!"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 text-black dark:text-white font-medium hover:text-gray-700 dark:hover:text-gray-300 transition-colors text-sm py-2"
                  >
                    <span>{t('vehicles.requestQuote').replace(' →', '')}</span>
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom note */}
        <div className="text-center mt-16 p-8 bg-gray-50 dark:bg-gray-700 rounded-2xl">
          <h3 className="text-xl font-light text-gray-900 dark:text-white mb-4">
            {t('vehicles.availability')}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed mb-6">
            {t('vehicles.availabilityDesc')}
          </p>
          <Button 
            onClick={onBookingClick}
            variant="outline"
            className="border-gray-900 dark:border-white text-gray-900 dark:text-white hover:bg-gray-900 dark:hover:bg-white hover:text-white dark:hover:text-gray-900 px-8 py-3"
          >
            {t('vehicles.bookNow')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Vehicles;
